import { Injectable } from '@angular/core';
import { isEmptyArray, isStringEmpty, stringEmptyDefault } from '@common/helpers';
import {
  ImmigrationSecondDocumentType,
  ImmigrationSecondDocumentTypeEnum,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { PassengerType } from '@common/interfaces';
import {
  ArrivalFlightInformation,
  ImmigrationInformationData,
  ImmigrationInformationParts,
  RegulatoryDetails,
  RegulatoryDetailsDestinationAddress,
  RegulatoryDetailsGreenCard,
  RegulatoryDetailsVisa,
} from '@common/interfaces/mybooking/modals/passenger-information-input/immigration-information.interface';
import { SelectDateInformation } from '@common/interfaces/mybooking/modals/passenger-information-input/passenger-Information-input-modal.interface';
import { GetOrderResponseData, GetOrderResponseDataTasks } from 'src/sdk-servicing';
import { Traveler } from 'src/sdk-servicing/model/traveler';
import { TravelerRegulatoryDetails } from 'src/sdk-servicing/model/travelerRegulatoryDetails';
import { PassengerInformationInputModalStoreService } from './passenger-information-input-modal-store.service';
import { PassengerInformationInputModalCommonService } from './passenger-information-input-modal.common.service';
import { convertStringToDate } from '@lib/helpers';

/**
 * 入国関連情報 service
 */
@Injectable()
export class ImmigrationInformationService {
  get pnr(): GetOrderResponseData | undefined {
    return this._s04StoreSvc.pnr();
  }
  constructor(
    private readonly _s04StoreSvc: PassengerInformationInputModalStoreService,
    private readonly _s04CommonSvc: PassengerInformationInputModalCommonService
  ) {}

  /**
   * 入国関連情報 初期値の作成
   * @param regulatoryDetails 入国関連情報
   * @param arrivalFlights
   * @param tasks @see GetOrderResponseDataTasks
   * @returns 入国関連情報 初期値
   */
  createData(
    regulatoryDetails: TravelerRegulatoryDetails,
    arrivalFlights: Array<ArrivalFlightInformation>,
    tasks?: GetOrderResponseDataTasks
  ): ImmigrationInformationData {
    let result: ImmigrationInformationData = this.initialImmigrationInformationData();

    const isHomeAddressRequired: boolean = tasks?.isHomeAddressRequired ?? false;
    const isChineseCitizenIdentificationNumberRegistrationRequired: boolean =
      tasks?.isChineseCitizenIdentificationNumberRegistrationRequired ?? false;
    const isRedressNumberRegistrationRequired: boolean = tasks?.isRedressNumberRegistrationRequired ?? false;
    const isKnownTravelerNumberRegistrationRequired: boolean =
      tasks?.isKnownTravelerNumberRegistrationRequired ?? false;

    if (regulatoryDetails?.homeAddressCountries?.[0]?.code) {
      result.homeAddressCountry.countries =
        (regulatoryDetails?.homeAddressCountries ?? []).map((homeAddressCountry) => ({
          code: homeAddressCountry.code ?? '',
        })) ?? [];
    }
    result.homeAddressCountry.isHomeAddressRequired = isHomeAddressRequired;

    if (regulatoryDetails?.chineseCitizenIdentificationNumbers?.[0]?.number) {
      result.chineseCitizenIdentificationNumber.numbers =
        (regulatoryDetails?.chineseCitizenIdentificationNumbers ?? []).map(
          (chineseCitizenIdentificationNumber) => chineseCitizenIdentificationNumber.number ?? ''
        ) ?? [];
    }
    result.chineseCitizenIdentificationNumber.isChineseCitizenIdentificationNumberRegistrationRequired =
      isChineseCitizenIdentificationNumberRegistrationRequired;

    if (regulatoryDetails?.redressNumbers?.[0]?.number) {
      result.redressNumber.numbers =
        regulatoryDetails?.redressNumbers?.map((redressNumber) => redressNumber?.number ?? '') ?? [];
    }
    result.redressNumber.isRedressNumberRegistrationRequired = isRedressNumberRegistrationRequired;

    if (regulatoryDetails?.knownTravellerNumbers?.[0]?.number) {
      result.knownTravellerNumber.numbers =
        regulatoryDetails?.knownTravellerNumbers?.map((knownTravellerNumber) => knownTravellerNumber?.number ?? '') ??
        [];
    }
    result.knownTravellerNumber.isKnownTravelerNumberRegistrationRequired = isKnownTravelerNumberRegistrationRequired;

    result.regulatoryDetails = this.createImmigrationRegulatoryDetails(regulatoryDetails, arrivalFlights, tasks);

    return result;
  }

  /**
   * 空の ImmigrationInformationData オブジェクトを初期化します。
   * @returns 初期化された ImmigrationInformationData オブジェクト。
   */
  initialImmigrationInformationData(): ImmigrationInformationData {
    return {
      homeAddressCountry: {
        countries: [{ code: '' }],
        isHomeAddressRequired: false,
      },
      chineseCitizenIdentificationNumber: {
        numbers: [''],
        isChineseCitizenIdentificationNumberRegistrationRequired: false,
      },
      regulatoryDetails: new Map([
        [
          '',
          {
            visas: [
              {
                number: '',
                issuanceCountryCode: '',
                expiryDate: '',
                isNoExpiration: false,
                registeredStatus: '',
              },
            ],
            greenCards: [
              {
                number: '',
                expiryDate: '',
                isNoExpiration: false,
              },
            ],
            destinationAddresses: [
              {
                lines: '',
                cityName: '',
                stateCode: '',
                zipCode: '',
                isTransit: false,
              },
            ],
            isVisaOrResidentAlienCardRegistrationRequired: false,
            isUSPassportNationalityCode: false,
          },
        ],
      ]),
      redressNumber: {
        numbers: [''],
        isRedressNumberRegistrationRequired: false,
      },
      knownTravellerNumber: {
        numbers: [''],
        isKnownTravelerNumberRegistrationRequired: false,
      },
      isError: false,
    };
  }

  /**
   * 到着便ごとに入国規制の詳細を作成します。
   * @param regulatoryDetails 旅行者の規制の詳細。
   * @param arrivalFlights 到着フライト情報の配列。
   * @param tasks オプションのタスク情報。
   * @returns キーとして到着フライト情報、値として規制の詳細を含む KeyValue ペアの配列。
   */
  createImmigrationRegulatoryDetails(
    regulatoryDetails: TravelerRegulatoryDetails,
    arrivalFlights: Array<ArrivalFlightInformation>,
    tasks?: GetOrderResponseDataTasks
  ): Map<string, RegulatoryDetails> {
    let result: Map<string, RegulatoryDetails> = new Map();

    arrivalFlights.forEach((arrivalFlight) => {
      const isVisaOrResidentAlienCardRegistrationRequired =
        tasks?.[arrivalFlight.flightId]?.isVisaOrResidentAlienCardRegistrationRequired ?? false;

      const isUSPassportNationalityCode =
        regulatoryDetails?.passports?.some(
          (passport) => passport.nationalityCode === 'USA' || passport.nationalityCode === 'US'
        ) ?? false;

      const lowerArrivalFlightCountry = arrivalFlight.arrivalCountry.toLocaleLowerCase();

      let _visas: Array<RegulatoryDetailsVisa> = [];
      let _greenCards: Array<RegulatoryDetailsGreenCard> = [
        {
          number: '',
          expiryDate: '',
          isNoExpiration: false,
        },
      ];
      let _destinationAddresses: Array<RegulatoryDetailsDestinationAddress> = [
        {
          lines: '',
          cityName: '',
          stateCode: '',
          zipCode: '',
          isTransit: false,
        },
      ];

      regulatoryDetails.visas?.forEach((visa) => {
        if (lowerArrivalFlightCountry === visa?.issuanceCountryCode?.toLocaleLowerCase()) {
          _visas.push({
            number: visa?.number ?? '',
            issuanceCountryCode: visa?.issuanceCountryCode ?? '',
            expiryDate: visa?.expiryDate ?? '',
            isNoExpiration: visa?.isNoExpiration ?? false,
            registeredStatus: visa?.registeredStatus ?? '',
          });
        }
      });

      if (_visas.length === 0) {
        _visas.push({
          number: '',
          issuanceCountryCode: '',
          expiryDate: '',
          isNoExpiration: false,
          registeredStatus: '',
        });
      }

      if (lowerArrivalFlightCountry === 'us') {
        _greenCards =
          regulatoryDetails.greenCards && regulatoryDetails.greenCards.length > 0
            ? regulatoryDetails.greenCards?.map((greenCard) => ({
                number: greenCard?.number ?? '',
                expiryDate: greenCard?.expiryDate ?? '',
                isNoExpiration: greenCard?.isNoExpiration ?? false,
              }))
            : [
                {
                  number: '',
                  expiryDate: '',
                  isNoExpiration: false,
                },
              ];

        if (regulatoryDetails.destinationAddresses?.[0]) {
          _destinationAddresses = regulatoryDetails.destinationAddresses
            ? regulatoryDetails.destinationAddresses?.map((address) => ({
                lines: address.lines ?? '',
                cityName: address.cityName ?? '',
                stateCode: address.stateCode ?? '',
                zipCode: address.zipCode ?? '',
                isTransit: address.isTransit ?? false,
              }))
            : [];
        }
      }

      result.set(arrivalFlight.arrivalCountry, {
        visas: _visas,
        greenCards: _greenCards,
        destinationAddresses: _destinationAddresses,
        isVisaOrResidentAlienCardRegistrationRequired: isVisaOrResidentAlienCardRegistrationRequired,
        isUSPassportNationalityCode,
      });
    });

    return result;
  }

  /**
   * 入国関連情報 設定値の作成
   * @param traveler 搭乗者情報
   * @param pnr PNR情報
   * @returns 入国関連情報 設定値
   */
  createParts(traveler: Traveler): ImmigrationInformationParts {
    // [1.13] 入国関連情報
    const isDisplayImmigration =
      // PNR情報取得API応答.data.orderEligibilities.travelerInput.isRegulatoryDetailsEligible = true、
      this.pnr?.orderEligibilities?.travelerInput?.isRegulatoryDetailsEligible === true &&
      // かつPNR情報取得API応答.data.nextActions.acknowledgeOrSelfReaccommodation≠trueの場合、表示する。
      this.pnr?.nextActions?.acknowledgeOrSelfReaccommodation !== true;
    // 但し、以下のすべてを満たす場合、非表示とする。
    const forceImmigrationHidden =
      // 1.PNR情報取得API応答.data.tasks[]に、isVisaOrResidentAlienCardRegistrationRequired=trueが存在しない
      !Boolean(
        this.pnr?.air?.bounds?.some((bound) =>
          bound.flights?.some(
            (flight) =>
              this.pnr?.tasks?.[stringEmptyDefault(flight.id)]?.isVisaOrResidentAlienCardRegistrationRequired === true
          )
        )
      ) &&
      // 2.PNR情報取得API応答.data.tasks.isHomeAddressRequired≠true
      this.pnr?.tasks?.isHomeAddressRequired !== true &&
      // 3.PNR情報取得API応答.data.tasks.isRedressNumberRegistrationRequired≠true
      this.pnr?.tasks?.isRedressNumberRegistrationRequired !== true &&
      // 4.PNR情報取得API応答.data.tasks.isKnownTravelerNumberRegistrationRequired≠true
      this.pnr?.tasks?.isKnownTravelerNumberRegistrationRequired !== true &&
      // 5.PNR情報取得API応答.data.tasks.isChineaseCitizenIdentificationNumberRegistrationRequired≠true
      this.pnr?.tasks?.isChineseCitizenIdentificationNumberRegistrationRequired !== true;
    const regulatoryDetails = traveler.regulatoryDetails;
    const isInfant = traveler.passengerTypeCode === PassengerType.INF;

    // 中国公民番号編集可否
    const isChineseCitizenIdentificationEditable = !(
      traveler?.regulatoryDetails?.chineseCitizenIdentificationNumbers &&
      traveler.regulatoryDetails.chineseCitizenIdentificationNumbers.length >= 2
    );

    // VISA情報編集可否
    const isVisaEditable = !(traveler?.regulatoryDetails?.visas && traveler.regulatoryDetails.visas.length >= 2);

    // グリーンカード情報編集可否
    const isGreenCardEditable = !(
      traveler?.regulatoryDetails?.greenCards && traveler.regulatoryDetails.greenCards.length >= 2
    );

    // 滞在先情報編集可否
    const isDestinationAddressEditable = !(
      traveler?.regulatoryDetails?.destinationAddresses && traveler.regulatoryDetails.destinationAddresses.length >= 2
    );

    // Redress Number編集可否
    const isRedressNumberEditable = !(
      // 当該travelers.regulatoryDetails.redressNumbersが存在する合、登録されているRedress Numberをラベル表示。複数登録されている場合は、複数表示する。
      (
        (traveler?.regulatoryDetails?.redressNumbers && traveler.regulatoryDetails.redressNumbers.length > 0) ||
        // PNR情報取得API応答.data.当該travelers.hasInfiniNamelessInfant＝trueの場合、又はPNR情報取得API応答.data.当該travelers.hasNamelessInfant＝trueの場合、未登録の旨をラベル表示する。
        (!traveler?.accompanyingTravelerId && traveler?.hasInfiniNamelessInfant) ||
        traveler?.hasNamelessInfant
      )
    );

    // Known Traveler Number編集可否
    const isKnownTravelerNumberEditable = !(
      // 当該travelers.regulatoryDetails.knownTravellerNumbersが存在する合、登録されているKnown Traveler Numberをラベル表示。複数登録されている場合は、複数表示する。
      (
        (traveler?.regulatoryDetails?.knownTravellerNumbers &&
          traveler.regulatoryDetails.knownTravellerNumbers.length > 0) ||
        // PNR情報取得API応答.data.当該travelers.hasInfiniNamelessInfant＝trueの場合、又はPNR情報取得API応答.data.当該travelers.hasNamelessInfant＝trueの場合、未登録の旨をラベル表示する。
        (!traveler?.accompanyingTravelerId && traveler?.hasInfiniNamelessInfant) ||
        traveler?.hasNamelessInfant
      )
    );

    // 居住国編集可否
    const isHomeAddressCountryEditable = !(
      traveler?.regulatoryDetails?.homeAddressCountries && traveler.regulatoryDetails.homeAddressCountries.length >= 2
    );

    const isVisaOrResidentAlienCardRegistrationRequired = Object.keys(this.pnr?.tasks ?? {}).find(
      (key) => this.pnr?.tasks?.[key]?.isVisaOrResidentAlienCardRegistrationRequired
    );
    const isDestinationAddressRegistered = Boolean(traveler?.regulatoryDetails?.isDestinationAddressRegistered);

    return {
      isInfant: isInfant,
      passengerIndex: -1,
      isChineseCitizenIdentificationEditable: isChineseCitizenIdentificationEditable,
      isVisaEditable: isVisaEditable,
      isGreenCardEditable: isGreenCardEditable,
      isDestinationAddressEditable: isDestinationAddressEditable,
      isRedressNumberEditable: isRedressNumberEditable,
      isKnownTravelerNumberEditable: isKnownTravelerNumberEditable,
      isHomeAddressCountryEditable: isHomeAddressCountryEditable,
      isImmigrationInfoRegistered: this.isImmigrationInfoRegistered(regulatoryDetails),
      isDisplay: isDisplayImmigration && !forceImmigrationHidden,
      registrationLabel: '',
      registrationClass: '',
      secondDocumentType: this.getSecondDocumentType(regulatoryDetails),
      isRequiredAccommodationInfo:
        !isStringEmpty(isVisaOrResidentAlienCardRegistrationRequired) && !isDestinationAddressRegistered,
        isinitNoConfirmAddress: isDestinationAddressRegistered
    };
  }

  /**
   * 入国情報が登録されているかどうかを確認します。
   * @param regulatoryDetails - 旅行者の規制の詳細。
   * @returns 入国管理情報が登録されているかどうかを示すブール値。
   */
  isImmigrationInfoRegistered(regulatoryDetails?: TravelerRegulatoryDetails): boolean {
    if (!regulatoryDetails) {
      return false;
    }

    return (
      !isEmptyArray(regulatoryDetails?.homeAddressCountries) ||
      !isEmptyArray(regulatoryDetails?.chineseCitizenIdentificationNumbers) ||
      !isEmptyArray(regulatoryDetails?.visas) ||
      !isEmptyArray(regulatoryDetails?.greenCards) ||
      !isEmptyArray(regulatoryDetails?.destinationAddresses) ||
      !isEmptyArray(regulatoryDetails?.redressNumbers) ||
      !isEmptyArray(regulatoryDetails?.knownTravellerNumbers)
    );
  }

  /**
   * 規制の詳細に基づいて 2 番目の入国書類の種類を取得します。
   * @param regulatoryDetails 旅行者の規制の詳細。
   * @returns 2番目の入国書類の種類。
   */
  getSecondDocumentType(regulatoryDetails?: TravelerRegulatoryDetails) {
    const greenCards = regulatoryDetails?.greenCards ?? [];
    const visas = regulatoryDetails?.visas?.filter((d) => d?.issuanceCountryCode === 'US') ?? [];
    if (greenCards?.length > 0 && !isStringEmpty(greenCards?.[0]?.number)) {
      return ImmigrationSecondDocumentType.GREEN_CARD;
    }

    if (visas?.length > 0 && !isStringEmpty(visas?.[0]?.number)) {
      return ImmigrationSecondDocumentType.VISA;
    }

    return ImmigrationSecondDocumentType.NOTHING;
  }

  /**
   * 書類の種類と有効期限に基づいて、2 番目の入国書類の選択日情報を取得します。
   * @param documentType 2番目の入国書類のタイプ。
   * @param expiryDate 2番目の入国書類の有効期限。
   * @param dateFromStr 第一出発日。（バリデーションで許容する一番過去の日付）
   * @returns 選択された日付情報。
   */
  getSecondDocumentSelectDate(
    documentType: ImmigrationSecondDocumentTypeEnum,
    expiryDateStr: string | undefined,
    dateFromStr: string | undefined,
  ): SelectDateInformation {
    const expiryDate = !expiryDateStr ? null : convertStringToDate(expiryDateStr);
    const dateFrom = !dateFromStr ? new Date() : convertStringToDate(dateFromStr);

    return {
      order: 'YMD',
      dateFrom: dateFrom,
      dateTo: this._s04CommonSvc.getExpiryDateMax(),
      initialDate:
        expiryDate !== null
          ? {
              year: expiryDate.getFullYear(),
              month: expiryDate.getMonth() + 1,
              day: expiryDate.getDate(),
            }
          : {
              year: undefined,
              month: undefined,
              day: undefined,
            },
    };
  }
}
